import { useState, useEffect } from 'react'
import { Form, Button, Alert, Spinner, InputGroup } from 'react-bootstrap'
import { Link, useNavigate } from 'react-router-dom'
import { GoogleLogin } from '@react-oauth/google'
import { useAuth } from '../contexts/AuthContext'
import './Home.css'

const EyeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
    <path d="M16 8s-3-5.5-8-5.5S0 8 0 8s3 5.5 8 5.5S16 8 16 8zM1.173 8a13.133 13.133 0 0 1 1.66-2.043C4.12 4.668 5.88 3.5 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.133 13.133 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755C11.879 11.332 10.119 12.5 8 12.5c-2.12 0-3.879-1.168-5.168-2.457A13.134 13.134 0 0 1 1.172 8z"/>
    <path d="M8 5.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5zM4.5 8a3.5 3.5 0 1 1 7 0 3.5 3.5 0 0 1-7 0z"/>
  </svg>
)

const EyeSlashIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
    <path d="M13.359 11.238C15.06 9.72 16 8 16 8s-3-5.5-8-5.5a7.028 7.028 0 0 0-2.79.588l.77.771A5.944 5.944 0 0 1 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.134 13.134 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755-.165.165-.337.328-.517.486l.708.709z"/>
    <path d="M11.297 9.176a3.5 3.5 0 0 0-4.474-4.474l.823.823a2.5 2.5 0 0 1 2.829 2.829l.822.822zm-2.943 1.299.822.822a3.5 3.5 0 0 1-4.474-4.474l.823.823a2.5 2.5 0 0 0 2.829 2.829z"/>
    <path d="M3.35 5.47c-.18.16-.353.322-.518.487A13.134 13.134 0 0 0 1.172 8l.195.288c.335.48.83 1.12 1.465 1.755C4.121 11.332 5.881 12.5 8 12.5c.716 0 1.39-.133 2.02-.36l.77.772A7.029 7.029 0 0 1 8 13.5C3 13.5 0 8 0 8s.939-1.721 2.641-3.238l.708.709zm10.296 8.884-12-12 .708-.708 12 12-.708.708z"/>
  </svg>
)

function Login() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [authMethod, setAuthMethod] = useState('email') // 'email' or 'phone'

  // Phone auth state
  const [phoneNumber, setPhoneNumber] = useState('')
  const [verificationCode, setVerificationCode] = useState('')
  const [codeSent, setCodeSent] = useState(false)
  const [countdown, setCountdown] = useState(0)

  const { login, googleLogin, sendPhoneCode, verifyPhoneCode, error } = useAuth()
  const navigate = useNavigate()

  // Countdown timer for code expiration
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000)
      return () => clearTimeout(timer)
    }
  }, [countdown])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    try {
      await login(email, password)
      navigate('/search')
    } catch (err) {
      // Error handled by context
    } finally {
      setLoading(false)
    }
  }

  const handleSendCode = async (e) => {
    e.preventDefault()
    setLoading(true)
    try {
      const result = await sendPhoneCode(phoneNumber)
      setCodeSent(true)
      setCountdown(result.expires_in || 300)
    } catch (err) {
      // Error handled by context
    } finally {
      setLoading(false)
    }
  }

  const handleVerifyCode = async (e) => {
    e.preventDefault()
    setLoading(true)
    try {
      await verifyPhoneCode(phoneNumber, verificationCode)
      navigate('/search')
    } catch (err) {
      // Error handled by context
    } finally {
      setLoading(false)
    }
  }

  const handleGoogleSuccess = async (credentialResponse) => {
    setLoading(true)
    try {
      await googleLogin(credentialResponse.credential)
      navigate('/search')
    } catch (err) {
      // Error handled by context
    } finally {
      setLoading(false)
    }
  }

  const handleGoogleError = () => {
    console.error('Google login failed')
  }

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  return (
    <div className="home-container">
      <div className="home-content">
        <h1 className="home-title">Login</h1>

        <div className="home-login-box">
          {error && <Alert variant="danger">{error}</Alert>}

          <div className="d-flex justify-content-center mb-3">
            <GoogleLogin
              onSuccess={handleGoogleSuccess}
              onError={handleGoogleError}
              useOneTap
              theme="outline"
              size="large"
              text="signin_with"
              shape="rectangular"
            />
          </div>

          <div className="home-divider">or</div>

          {/* Auth Method Toggle */}
          <div className="home-auth-toggle">
            <Button
              variant={authMethod === 'email' ? 'light' : 'outline-light'}
              size="sm"
              className="me-2"
              onClick={() => setAuthMethod('email')}
            >
              Email
            </Button>
            <Button
              variant={authMethod === 'phone' ? 'light' : 'outline-light'}
              size="sm"
              onClick={() => setAuthMethod('phone')}
            >
              Phone
            </Button>
          </div>

          {authMethod === 'email' ? (
            <Form onSubmit={handleSubmit}>
              <Form.Group className="mb-3">
                <Form.Control
                  type="email"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={loading}
                  className="home-input"
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <InputGroup>
                  <Form.Control
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={loading}
                    className="home-input home-password-input"
                  />
                  <Button
                    variant="link"
                    className="home-password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeSlashIcon /> : <EyeIcon />}
                  </Button>
                </InputGroup>
              </Form.Group>
              <Button variant="primary" type="submit" className="w-100 home-button" disabled={loading}>
                {loading ? <Spinner size="sm" /> : 'Log In'}
              </Button>
            </Form>
          ) : (
            <>
              {!codeSent ? (
                <Form onSubmit={handleSendCode}>
                  <Form.Group className="mb-3">
                    <Form.Control
                      type="tel"
                      placeholder="+1234567890"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                      required
                      disabled={loading}
                      className="home-input"
                    />
                  </Form.Group>
                  <Button variant="primary" type="submit" className="w-100 home-button" disabled={loading}>
                    {loading ? <Spinner size="sm" /> : 'Send Code'}
                  </Button>
                </Form>
              ) : (
                <Form onSubmit={handleVerifyCode}>
                  <Form.Group className="mb-3">
                    <Form.Control
                      type="tel"
                      value={phoneNumber}
                      disabled
                      className="home-input"
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <InputGroup>
                      <Form.Control
                        type="text"
                        placeholder="123456"
                        value={verificationCode}
                        onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        required
                        disabled={loading}
                        maxLength={6}
                        className="home-input"
                      />
                      {countdown > 0 && (
                        <InputGroup.Text className="home-countdown">{formatTime(countdown)}</InputGroup.Text>
                      )}
                    </InputGroup>
                  </Form.Group>
                  <Button variant="primary" type="submit" className="w-100 home-button" disabled={loading || verificationCode.length !== 6}>
                    {loading ? <Spinner size="sm" /> : 'Verify & Log In'}
                  </Button>
                  <div className="home-phone-links">
                    <Button
                      variant="link"
                      className="home-phone-link"
                      onClick={() => {
                        setCodeSent(false)
                        setVerificationCode('')
                      }}
                      disabled={loading}
                    >
                      Change number
                    </Button>
                    {countdown === 0 && (
                      <Button
                        variant="link"
                        className="home-phone-link"
                        onClick={handleSendCode}
                        disabled={loading}
                      >
                        Resend code
                      </Button>
                    )}
                  </div>
                </Form>
              )}
            </>
          )}

          <div className="home-links">
            <Link to="/forgot-password">Forgot password?</Link>
            <span className="home-separator">|</span>
            <Link to="/register">Create account</Link>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Login
